import type { ReactNode, FunctionComponent } from 'react';
import { Children, cloneElement } from 'react';
import Target from './AccordionTarget';
import Body from './AccordionBody';
import useAccordion from './useAccordion';
import type { AccordionTargetProps, AccordionBodyProps } from './useAccordion';
import styles from './Accordion.module.less';

type RootProps = {
  /**
   * Required for a11y.
   */
  id: string;
  /**
   * Optionally set if the accordion should be open from the start (@default true)
   */
  isInitiallyOpen?: boolean;
  /**
   * Children components to be passed in, typically an Accordion.Target + Accordion.Body.
   */
  children: ReactNode;
};

type AccordionComponent = FunctionComponent<RootProps> & {
  //  Note that while these components do take isOpen as a prop,
  //  we don't want to expose that as part of this compound component because it's provided for free.
  Target: FunctionComponent<AccordionTargetProps>;
  Body: FunctionComponent<AccordionBodyProps>;
};

/**
 * A basic styled reusable single Accordion component.
 * If you want to style your own, check out the useAccordion hook.
 *
 * Uses the compound component pattern so that you can include your own children.
 * Provide the isInitiallyOpen if you need it to be open/closed on mount (defaults to open).
 * All accordions require a unique ID, for a11y purposes.
 *
 * @example
 * <Accordion id="my-accordion">
 *   <Accordion.Target>
 *     Click me to open!
 *   </Accordion.Target>
 *   <Accordion.Body>
 *     🎉 Surprise!
 *     <div>
 *       Anything can go in here!
 *     </div>
 *   </Accordion.Body>
 * </Accordion>
 */
const Accordion: AccordionComponent = ({ children, id, isInitiallyOpen = true }) => {
  const { isOpen, targetProps, bodyProps } = useAccordion({ id, isInitiallyOpen });

  return (
    <div className={styles.accordion}>
      {Children.map(children, child => {
        if (child?.type?.displayName === Target.displayName) {
          return cloneElement(child as any, {
            ...targetProps,
            isOpen,
            className: styles.target,
          });
        }

        if (child?.type?.displayName === Body.displayName) {
          return cloneElement(child as any, {
            ...bodyProps,
            isOpen,
            className: styles.body,
          });
        }
        return null;
      })}
    </div>
  );
};

Accordion.Target = Target;
Accordion.Body = Body;

export default Accordion;
